import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@tellescope/ui";
import type { VitalsFilters } from "../../hooks/useVitalsTable";
import type { ColumnConfigFormProps } from "../tables/ColumnConfigForm";
import type { UseTableViewConfigResult } from "../../hooks/useTableViewConfig";
import type { SortingState } from "@tanstack/react-table";
import { VitalsTableConfigDialogContent } from "./VitalsTableConfigDialogContent";

export interface VitalsTableConfigDialogProps
  extends Pick<UseTableViewConfigResult<VitalsFilters>, "onApplyConfig" | "activeFilters"> {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;
  description?: string;
  columnConfigFormProps: Pick<
    ColumnConfigFormProps,
    "columnVisibility" | "orderedVisibleColumnIds" | "combinedFieldToColumnConfigMap" | "title"
  > & {
    sorting?: SortingState;
    setSorting?: Dispatch<SetStateAction<SortingState>>;
  };
  onClearAll?: () => void;
  isDirty?: boolean;
  sourceOptions?: string[];
}

export const VitalsTableConfigDialog = ({
  onApplyConfig,
  activeFilters,
  title,
  description,
  columnConfigFormProps: {
    columnVisibility,
    orderedVisibleColumnIds,
    combinedFieldToColumnConfigMap,
    title: columnConfigTitle,
    sorting,
    setSorting,
  },
  isDirty: providedIsDirty,
  sourceOptions,
  open,
  onOpenChange,
  onClearAll,
}: VitalsTableConfigDialogProps) => {
  const { t } = useTranslation("vitals");

  const handleClose = () => onOpenChange?.(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="min-w-[700px] max-h-[90vh] flex flex-col p-0 overflow-hidden gap-1">
        <div className="p-4 space-y-2">
          <DialogTitle>{title ?? t("Filter Vitals")}</DialogTitle>
          <DialogDescription>{description ?? t("Apply filters to narrow down the vitals records")}</DialogDescription>
        </div>
        <VitalsTableConfigDialogContent
          onApplyConfig={onApplyConfig}
          activeFilters={activeFilters}
          columnVisibility={columnVisibility}
          orderedVisibleColumnIds={orderedVisibleColumnIds}
          combinedFieldToColumnConfigMap={combinedFieldToColumnConfigMap}
          columnConfigTitle={columnConfigTitle}
          sorting={sorting}
          setSorting={setSorting}
          providedIsDirty={providedIsDirty}
          sourceOptions={sourceOptions}
          onClearAll={onClearAll}
          onClose={handleClose}
        />
      </DialogContent>
    </Dialog>
  );
};
